import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Table, Tag, Button, Typography, Popconfirm, Upload, App, Space } from 'antd';
import { UploadOutlined, DownloadOutlined, CameraOutlined, ThunderboltOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd';
import { api } from '../api';
import { t } from '../i18n';
import { HintTooltip as Tooltip } from '../components/HintTooltip';
import { fmtDateTime } from '../time';

type Backup = Awaited<ReturnType<typeof api.listBackups>>[number];

/** Guarded parse so one malformed summary row can't crash the whole table. */
function parseSummaryJson(j: string | null): { bom?: string; level?: string } | null {
  if (!j) return null;
  try {
    return JSON.parse(j) as { bom?: string; level?: string };
  } catch {
    return null;
  }
}

// Capture kind → human label + colour for the disk's backup list.
const SOURCE_LABEL: Record<string, { label: string; color: string }> = {
  pre_apply: { label: 'Before Flashing', color: 'orange' },
  post_apply_snapshot: { label: 'After Flashing', color: 'green' },
  inventory_snapshot: { label: 'Reported', color: 'blue' },
  manual: { label: 'Manual', color: 'purple' },
};

export function BackupHistoryModal({
  sn,
  sessionId,
  canRestore,
  isAdmin,
  isSuper = false,
  onClose,
}: {
  sn: string;
  sessionId: string;
  canRestore: boolean;
  /** Downloading disk dumps (as .hwrsv) is admin-only (no client exfiltration). */
  isAdmin: boolean;
  /** Deleting a backup outright is a superadmin-only power. */
  isSuper?: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const q = useQuery({ queryKey: ['backups', sn], queryFn: () => api.listBackups(sn) });

  const restore = useMutation({
    mutationFn: (backupId: string) => api.restore(sessionId, { [sn]: backupId }),
    onSuccess: () => {
      message.success(t('flashSent'));
      qc.invalidateQueries({ queryKey: ['session', sessionId] });
      onClose();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const upload = useMutation({
    mutationFn: (vars: { bytes: ArrayBuffer; label: string }) =>
      api.uploadBackup(sn, vars.bytes, vars.label),
    onSuccess: () => {
      message.success(t('backupUploadOk'));
      qc.invalidateQueries({ queryKey: ['backups', sn] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const snapshot = useMutation({
    mutationFn: () => api.manualBackup(sessionId, sn),
    onSuccess: () => {
      message.success(t('backupSnapshotOk'));
      qc.invalidateQueries({ queryKey: ['backups', sn] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const del = useMutation({
    mutationFn: (backupId: string) => api.deleteBackup(backupId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backups', sn] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const beforeUpload = (file: UploadFile): boolean => {
    const blob = file as unknown as Blob;
    // Send the raw .hwrsv bytes as octet-stream; the server parses + stores it.
    void blob
      .arrayBuffer()
      .then((bytes) => upload.mutate({ bytes, label: file.name }))
      .catch((e: Error) => message.error(e.message));
    return false; // handle manually, don't auto-POST
  };

  const fmt = fmtDateTime;

  const columns: ColumnsType<Backup> = [
    { title: t('backupCreated'), dataIndex: 'createdAt', render: (v: number) => <Typography.Text type="secondary">{fmt(v)}</Typography.Text> },
    {
      title: t('backupSource'),
      dataIndex: 'source',
      // One snapshot can serve several stages (e.g. the inventory capture was
      // byte-identical to the pre-flash read) — show a tag for each.
      render: (s: string, row: Backup) => {
        const stages = [s, ...(row.alsoSources ?? [])];
        return (
          <Space size={4} wrap>
            {stages.map((st) => {
              const m = SOURCE_LABEL[st] ?? { label: st, color: 'default' };
              return <Tag key={st} color={m.color}>{m.label}</Tag>;
            })}
          </Space>
        );
      },
    },
    {
      title: t('backupSummary'),
      dataIndex: 'parsedSummaryJson',
      render: (j: string | null) => {
        const s = parseSummaryJson(j);
        return s ? <span>BOM=<Typography.Text code>{s.bom}</Typography.Text> · Level=<Typography.Text code>{s.level}</Typography.Text></span> : <Typography.Text type="secondary">—</Typography.Text>;
      },
    },
    { title: t('deviceLabel'), dataIndex: 'label', render: (l: string | null) => l ?? <Typography.Text type="secondary">—</Typography.Text> },
    ...(isAdmin || canRestore || isSuper
      ? [
          {
            title: t('colActions'),
            key: 'actions',
            render: (_: unknown, b: Backup) => (
              <Space wrap size={4}>
                {isAdmin && (
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    href={`/api/admin/v1/disk-backups/${b.id}/download`}
                    download
                  >
                    {t('historyDownload')}
                  </Button>
                )}
                {canRestore && (
                  <Popconfirm title={t('restoreConfirm')} onConfirm={() => restore.mutate(b.id)}>
                    <Button danger size="small" icon={<ThunderboltOutlined />} loading={restore.isPending}>
                      {t('flashThis')}
                    </Button>
                  </Popconfirm>
                )}
                {isSuper && (
                  <Popconfirm title={t('backupDeleteConfirm')} okButtonProps={{ danger: true }} onConfirm={() => del.mutate(b.id)}>
                    <Button danger size="small" icon={<DeleteOutlined />} loading={del.isPending}>
                      {t('backupDelete')}
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          } as ColumnsType<Backup>[number],
        ]
      : []),
  ];

  return (
    <Modal
      open
      width={760}
      title={
        <Space>
          {t('backupsTitle')}
          <Typography.Text code>{sn}</Typography.Text>
        </Space>
      }
      onCancel={onClose}
      footer={
        <Space>
          {canRestore && (
            <Button icon={<CameraOutlined />} loading={snapshot.isPending} onClick={() => snapshot.mutate()}>
              {t('backupSnapshotBtn')}
            </Button>
          )}
          {canRestore && (
            <Tooltip title={t('backupUploadHint')}>
              <Upload accept=".hwrsv" showUploadList={false} beforeUpload={beforeUpload}>
                <Button icon={<UploadOutlined />} loading={upload.isPending}>
                  {t('backupUploadBtn')}
                </Button>
              </Upload>
            </Tooltip>
          )}
          <Button type="primary" onClick={onClose}>
            {t('confirmApplyCancel')}
          </Button>
        </Space>
      }
    >
      <Table<Backup>
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={q.data ?? []}
        loading={q.isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('emptyList') }}
      />
    </Modal>
  );
}
